import { RenderManager } from '../../../core/RenderManager';
import { Piece } from '../entities/Piece';

export interface GridBlock {
  filled: boolean;
  color: string;
}

export class Grid {
  private width: number;
  private height: number;
  private grid: GridBlock[][];
  private renderManager: RenderManager;

  constructor(width: number, height: number, renderManager: RenderManager) {
    this.width = width;
    this.height = height;
    this.renderManager = renderManager;
    this.grid = [];
    this.initializeGrid();
  }

  private initializeGrid(): void {
    this.grid = [];
    for (let y = 0; y < this.height; y++) {
      this.grid[y] = [];
      for (let x = 0; x < this.width; x++) {
        this.grid[y][x] = {
          filled: false,
          color: '#000000'
        };
      }
    }
  }

  public clear(): void {
    this.initializeGrid();
  }

  public getWidth(): number {
    return this.width;
  }

  public getHeight(): number {
    return this.height;
  }

  public isValidPosition(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  public isBlockFilled(x: number, y: number): boolean {
    if (!this.isValidPosition(x, y)) {
      return true; // Treat out-of-bounds as filled
    }
    return this.grid[y][x].filled;
  }

  public setBlock(x: number, y: number, filled: boolean, color: string = '#FFFFFF'): void {
    if (!this.isValidPosition(x, y)) return;
    
    this.grid[y][x] = {
      filled,
      color: filled ? color : '#000000'
    };
  }

  public getBlock(x: number, y: number): GridBlock | null {
    if (!this.isValidPosition(x, y)) return null;
    return { ...this.grid[y][x] };
  }

  public checkCollision(piece: Piece): boolean {
    const filledPositions = piece.getFilledPositions();
    
    for (const pos of filledPositions) {
      // Check bounds
      if (pos.x < 0 || pos.x >= this.width || pos.y >= this.height) {
        return true;
      }
      
      // Allow pieces to spawn above the grid (negative Y is OK)
      if (pos.y < 0) {
        continue;
      }
      
      // Check collision with existing blocks
      if (this.grid[pos.y][pos.x].filled) {
        return true;
      }
    }
    
    return false;
  }

  public placePiece(piece: Piece): void {
    const filledPositions = piece.getFilledPositions();
    const color = piece.getColor();
    
    for (const pos of filledPositions) {
      if (this.isValidPosition(pos.x, pos.y)) {
        this.setBlock(pos.x, pos.y, true, color);
      }
    }
  }

  public clearLines(): number {
    const fullLines: number[] = [];
    
    // Find all full lines
    for (let y = 0; y < this.height; y++) {
      let isFullLine = true;
      for (let x = 0; x < this.width; x++) {
        if (!this.grid[y][x].filled) {
          isFullLine = false;
          break;
        }
      }
      if (isFullLine) {
        fullLines.push(y);
      }
    }
    
    // Remove full lines and add empty lines at top
    if (fullLines.length > 0) {
      // Create a new grid without the full lines
      const newGrid: GridBlock[][] = [];
      
      // Add empty lines at the top
      for (let i = 0; i < fullLines.length; i++) {
        const emptyLine: GridBlock[] = [];
        for (let x = 0; x < this.width; x++) {
          emptyLine.push({
            filled: false,
            color: '#000000'
          });
        }
        newGrid.push(emptyLine);
      }
      
      // Add remaining lines from bottom to top
      for (let y = 0; y < this.height; y++) {
        if (!fullLines.includes(y)) {
          newGrid.push([...this.grid[y]]);
        }
      }
      
      this.grid = newGrid;
    }
    
    return fullLines.length;
  }

  public isLineFull(lineIndex: number): boolean {
    if (lineIndex < 0 || lineIndex >= this.height) return false;
    
    for (let x = 0; x < this.width; x++) {
      if (!this.grid[lineIndex][x].filled) {
        return false;
      }
    }
    return true;
  }

  public getLineCount(): number {
    let count = 0;
    for (let y = 0; y < this.height; y++) {
      if (this.isLineFull(y)) {
        count++;
      }
    }
    return count;
  }

  public getHeightAt(x: number): number {
    if (x < 0 || x >= this.width) return 0;
    
    for (let y = 0; y < this.height; y++) {
      if (this.grid[y][x].filled) {
        return this.height - y;
      }
    }
    return 0;
  }

  public getAverageHeight(): number {
    let totalHeight = 0;
    for (let x = 0; x < this.width; x++) {
      totalHeight += this.getHeightAt(x);
    }
    return totalHeight / this.width;
  }

  public getHoleCount(): number {
    let holeCount = 0;
    
    for (let x = 0; x < this.width; x++) {
      let foundBlock = false;
      for (let y = 0; y < this.height; y++) {
        if (this.grid[y][x].filled) {
          foundBlock = true;
        } else if (foundBlock) {
          // Found empty space below a filled block = hole
          holeCount++;
        }
      }
    }
    
    return holeCount;
  }

  public render(offsetX: number, offsetY: number, blockSize: number): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const block = this.grid[y][x];
        
        if (block.filled) {
          const blockX = offsetX + x * blockSize;
          const blockY = offsetY + y * blockSize;
          
          // Draw filled block
          this.renderManager.fillRect(
            blockX + 1,
            blockY + 1,
            blockSize - 2,
            blockSize - 2,
            block.color
          );
          
          // Add highlight for 3D effect
          this.renderManager.strokeRect(
            blockX + 1,
            blockY + 1,
            blockSize - 2,
            blockSize - 2,
            '#FFFFFF',
            1
          );
        }
      }
    }
  }

  public isEmpty(): boolean {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.grid[y][x].filled) {
          return false;
        }
      }
    }
    return true;
  }

  public getTopFilledRow(): number {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.grid[y][x].filled) {
          return y;
        }
      }
    }
    return this.height; // No filled blocks
  }

  // Debug method to print grid state
  public debugPrint(): void {
    console.log('=== GRID STATE ===');
    for (let y = 0; y < this.height; y++) {
      let line = '';
      for (let x = 0; x < this.width; x++) {
        line += this.grid[y][x].filled ? '█' : '·';
      }
      console.log(`${y.toString().padStart(2)}: ${line}`);
    }
    console.log('==================');
  }
}